import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/layout/Navbar";
import { toast } from "sonner";
import { ChevronRight, ChevronLeft, Upload, X, Sparkles, Undo2 } from "lucide-react";
import { streamAI } from "@/lib/aiStream";

const STEPS = ["Category", "Project", "Photos", "Location", "Contact"];

export default function PostJob() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [cats, setCats] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiPrev, setAiPrev] = useState(null);
  const [form, setForm] = useState({
    category_id: location.state?.category_id || "",
    title: "",
    description: "",
    location: "",
    zip_code: "",
    budget_min: "",
    budget_max: "",
    timeline: "flexible",
    customer_phone: "",
    photos: [],
  });
  const quizSessionId = location.state?.quiz_session_id || null;

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const next = () => {
    if (step === 0 && !form.category_id) return toast.error("Pick a category");
    if (step === 1 && (!form.title || !form.description)) return toast.error("Fill in title and description");
    if (step === 3 && (!form.location || !form.zip_code)) return toast.error("Add location");
    setStep(Math.min(step + 1, STEPS.length - 1));
  };
  const back = () => setStep(Math.max(0, step - 1));

  const improveWithAI = async () => {
    if (!form.category_id) return toast.error("Pick a category first");
    if (form.description.trim().length < 10 && form.title.trim().length < 5)
      return toast.error("Write a few words first, then let AI polish it");
    setAiBusy(true);
    setAiPrev(form.description);
    try {
      await streamAI(
        "/ai/job-description",
        {
          category_id: form.category_id,
          title: form.title,
          description: form.description,
          location: form.location,
          zip_code: form.zip_code,
          budget_min: form.budget_min ? Number(form.budget_min) : null,
          budget_max: form.budget_max ? Number(form.budget_max) : null,
          timeline: form.timeline,
        },
        (full) => set("description", full)
      );
      toast.success("Rewritten. Edit anything that isn't right.");
    } catch (err) {
      set("description", aiPrev);
      setAiPrev(null);
      toast.error(err.message || "AI rewrite failed");
    } finally {
      setAiBusy(false);
    }
  };

  const undoAI = () => {
    set("description", aiPrev);
    setAiPrev(null);
  };

  const onPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files.slice(0, 5 - form.photos.length)) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data.path);
      }
      setForm((f) => ({ ...f, photos: [...f.photos, ...uploaded] }));
      toast.success(`${uploaded.length} photo(s) uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed (storage may not be configured)");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (p) => setForm((f) => ({ ...f, photos: f.photos.filter((x) => x !== p) }));

  const submit = async () => {
    try {
      const payload = { ...form };
      payload.budget_min = payload.budget_min ? Number(payload.budget_min) : null;
      payload.budget_max = payload.budget_max ? Number(payload.budget_max) : null;
      if (quizSessionId) payload.quiz_session_id = quizSessionId;
      await api.post("/jobs", payload);
      toast.success("Project posted! Pros will contact you soon.");
      navigate("/customer");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post job");
    }
  };

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <div className="max-w-[900px] mx-auto px-6 md:px-12 py-12">
        <div className="overline mb-3">// Post a project</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter">Tell us what you need</h1>

        <div className="mt-10 grid grid-cols-4 gap-0 border-2 border-ink">
          {STEPS.map((s, i) => (
            <div key={s} className={`border-r-2 border-ink last:border-r-0 p-3 text-center font-mono text-xs uppercase tracking-widest ${i === step ? "bg-ink text-acid" : i < step ? "bg-acid" : "bg-white"}`}>
              {String(i + 1).padStart(2, "0")} · {s}
            </div>
          ))}
        </div>

        <div className="brut-card mt-8 p-8 bg-white">
          {step === 0 && (
            <div>
              <label className="overline">Choose your trade</label>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                {cats.map((c) => (
                  <button
                    key={c.category_id}
                    type="button"
                    onClick={() => set("category_id", c.category_id)}
                    className={`border-2 border-ink p-4 text-left font-mono text-xs uppercase tracking-widest font-bold ${form.category_id === c.category_id ? "bg-klein text-white" : "bg-white hover:bg-acid"}`}
                    data-testid={`postjob-cat-${c.slug}`}
                  >
                    {c.name}
                    <div className="font-normal text-[10px] mt-1">${c.lead_price.toFixed(0)}/lead</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <label className="overline block mb-2">Project title</label>
                <input className="brut-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Replace kitchen faucet" data-testid="postjob-title" />
              </div>
              <div>
                <div className="flex items-end justify-between gap-3 mb-2">
                  <label className="overline">Describe the work</label>
                  <button type="button" onClick={improveWithAI} disabled={aiBusy} className="brut-btn text-[10px] py-2 disabled:opacity-40" data-testid="postjob-ai-improve">
                    <Sparkles className={`w-3 h-3 mr-1 ${aiBusy ? "animate-pulse" : ""}`} />
                    {aiBusy ? "Writing..." : "Improve with AI"}
                  </button>
                </div>
                <textarea className="brut-input min-h-[140px]" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Tell us what you need done, materials, urgency..." data-testid="postjob-desc" />
                {aiPrev !== null && !aiBusy && (
                  <button type="button" onClick={undoAI} className="brut-btn text-[10px] py-2 mt-2" data-testid="postjob-ai-undo">
                    <Undo2 className="w-3 h-3 mr-1" /> Undo AI rewrite
                  </button>
                )}
                <p className="font-mono text-[10px] mt-2 text-muted-foreground">
                  Rough notes are fine — Claude turns them into a job post pros can quote from.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-2">Budget min ($)</label>
                  <input type="number" className="brut-input" value={form.budget_min} onChange={(e) => set("budget_min", e.target.value)} data-testid="postjob-budget-min" />
                </div>
                <div>
                  <label className="overline block mb-2">Budget max ($)</label>
                  <input type="number" className="brut-input" value={form.budget_max} onChange={(e) => set("budget_max", e.target.value)} data-testid="postjob-budget-max" />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">Timeline</label>
                <div className="grid grid-cols-3 gap-0 border-2 border-ink">
                  {["asap", "weeks", "flexible"].map((t) => (
                    <button key={t} type="button" onClick={() => set("timeline", t)} className={`py-3 font-mono uppercase text-xs tracking-widest font-bold border-l-2 first:border-l-0 border-ink ${form.timeline === t ? "bg-ink text-acid" : "bg-white"}`} data-testid={`postjob-timeline-${t}`}>{t}</button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {step === 2 && (
            <div>
              <label className="overline">Add photos (optional, max 5)</label>
              <p className="text-sm text-muted-foreground mt-2">Photos help pros give you accurate quotes faster.</p>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                {form.photos.map((p) => (
                  <div key={p} className="relative border-2 border-ink aspect-square bg-bone">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removePhoto(p)} className="absolute top-1 right-1 bg-ink text-acid w-6 h-6 flex items-center justify-center border-2 border-ink" data-testid={`photo-remove-${p}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {form.photos.length < 5 && (
                  <label className="border-2 border-dashed border-ink aspect-square flex flex-col items-center justify-center bg-white hover:bg-acid cursor-pointer" data-testid="photo-upload">
                    <Upload className="w-6 h-6" />
                    <span className="font-mono text-[10px] uppercase tracking-widest mt-2">{uploading ? "Uploading..." : "Add"}</span>
                    <input type="file" multiple accept="image/*" className="hidden" onChange={onPhoto} disabled={uploading} />
                  </label>
                )}
              </div>
              <p className="font-mono text-[10px] mt-3 text-muted-foreground">{form.photos.length}/5 photos · JPG/PNG/WebP/GIF · max 10MB each</p>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-5">
              <div>
                <label className="overline block mb-2">City / Address</label>
                <input className="brut-input" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Austin, TX" data-testid="postjob-location" />
              </div>
              <div>
                <label className="overline block mb-2">ZIP code</label>
                <input className="brut-input" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} placeholder="78701" data-testid="postjob-zip" />
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-5">
              <div>
                <label className="overline block mb-2">Phone (so pros can call you)</label>
                <input className="brut-input" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} placeholder="+1 555 123 4567" data-testid="postjob-phone" />
              </div>
              <div className="p-4 border-2 border-ink bg-acid">
                <div className="overline mb-2">Review</div>
                <div className="font-mono text-xs space-y-1">
                  <div>Category: {cats.find((c) => c.category_id === form.category_id)?.name}</div>
                  <div>Title: {form.title}</div>
                  <div>Location: {form.location}, {form.zip_code}</div>
                  <div>Budget: ${form.budget_min || "?"} – ${form.budget_max || "?"}</div>
                  <div>Timeline: {form.timeline}</div>
                  <div>Photos: {form.photos.length}</div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 flex justify-between gap-3">
            <button type="button" onClick={back} disabled={step === 0} className="brut-btn disabled:opacity-30" data-testid="postjob-back">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button type="button" onClick={next} className="brut-btn-primary" data-testid="postjob-next">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            ) : (
              <button type="button" onClick={submit} className="brut-btn-acid" data-testid="postjob-submit">Post Project</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
